import { Dispatch, Action } from 'redux';
import { AxiosResponse } from 'axios';
import { History } from 'history';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'data/root.reducers';
import { ServerResponse } from 'data/types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';
import { CollectionFunctionGroup } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { EnabledService, SupportedFeatures } from 'data/supportedFeatures/supportedFeatures.types';
import { Network } from 'data/network/network.types';
import { EWARRANT_ROUTE } from '../../constants';
import * as api from './eWarrant.api';
import { adaptEWarrantDetailResponse } from './eWarrant.adapter';
import {
  GENERATE_WARRANT,
  UPDATE_EWARRANT_AUTH_STATUS,
  UPDATE_EWARRANT_INTERCEPT_STATUS,
  UPDATE_EWARRANT_TASK_STATUS,
  EWarrantDecisionStatus,
  EWarrantInterceptStatus,
  EWarrantInterceptStatusValue,
  EWarrant,
  EWarrantActionTypes,
  EWarrantDetailResponse,
  EWarrantDocument,
  EWarrantDocumentStatusPayload,
  EWarrantGenerateTemplate,
  EWarrantGridRowData,
  EWarrantListItem,
  EWarrantStatus,
  EWarrantTaskStatus,
  EWarrantThunkAction,
  GENERATE_EWARRANT_FULFILLED,
  GET_EWARRANT_DETAIL,
  GET_EWARRANT_LIST,
  GO_TO_EWARRANT_LIST,
  GO_TO_EWARRANT_VIEW,
  SELECT_EWARRANT,
  SelectEWarrantAction,
} from './eWarrant.types';

/**
 * Serve the e-warrant list from EWARRANT_MOCK_LIST below instead of reading it from
 * the backend. Flip to true to work on the pages without a Xcipio behind them, or to
 * get back the richer fields (documents, services, targets) the list endpoint does
 * not return yet.
 *
 * Only affects the list read — approve/reject/generateEWarrant are still mocked
 * regardless, and updateInterceptStatus/generateWarrant always call the backend.
 */
export const USE_EWARRANT_MOCK_LIST = false;

/* ==========================================================================
 * MOCK DATA
 *
 * Each entry is an e-warrant as it would be stored after parsing an inbound
 * ETSI TS 103 120 HI1Message dispatched by the LEA application. It stands in for
 * the response of the e-warrant API when USE_EWARRANT_MOCK_LIST is on, and is
 * still what the operations below that have no backend yet resolve against.
 *
 * It lives in the actions file on purpose, so it is easy to edit while the
 * pages are being built.
 * ========================================================================== */

export const EWARRANT_MOCK_LIST: EWarrant[] = [
  {
    id: 'csw-3006',
    status: EWarrantStatus.Approved,
    interceptStatus: EWarrantInterceptStatusValue.NotCreated,
    decidedAt: '2026-07-02T11:22:10Z',
    decidedBy: 'csp.operator',
    decisionNote: 'Provisioned on LI gateway VZW-LI-GW-01, task active.',
    authorisation: {
      HI1OBJECTID: 'csw-3006',
      SENDERID: 'US-7F2A91C3',
      name: 'Intercept 2:26-mc-00489 · Verizon Wireless',
      description: 'Title III voice + data intercept, Operation NORTHSTAR',
      sourceWarrantId: 'ew-2041',
      sourceAgency: 'US-7F2A91C3',
      receivingCsp: 'Verizon Wireless',
      receivedAt: '2026-07-02T11:05:00Z',
      reference: '2:26-mc-00489',
      priority: 'Normal',
      legalType: '',
      typeOfCase: 'DrugsOrDrugTrafficking',
      desiredStatus: 'AwaitingApproval',
      startTime: '2026-07-02T00:00:00Z',
      endTime: '2026-08-01T23:59:59Z',
      creationTimestamp: '2026-07-01T00:00:00Z',
      jurisdiction: 'District of Arizona',
      legalEntity: 'DEA — Phoenix Field Division',
      cspId: 'US-VZW-01',
      countryCode: 'US',
      flags: [],
      approval: {
        type: 'Creation',
        reference: '2:26-mc-00489',
        approverName: 'Hon. R. T. Marsden',
        approverRole: 'Authorising Judicial Officer',
        approverEmail: 'r.marsden@azd.uscourts.gov',
        approverPhone: '+1 602 322 7200',
        timestamp: '2026-07-01T00:00:00Z',
        isEmergency: false
      },
      manualInformation: 'Minimization instructions per attached affidavit ¶14.'
    },
    documents: [
      {
        docId: 'doc-b1',
        documentName: 'TitleIII_Pkg_2-26-mc-00489_SIGNED.pdf',
        documentType: 'Warrant',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: '9f2c41ab77dea0031bb4c7e2119d8fa0',
        documentLocation: '/SS8/eWarrant/doc-b1/TitleIII_Pkg_2-26-mc-00489_SIGNED.pdf',
        sizeBytes: 1380352,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Approved
      },
      {
        docId: 'doc-b2',
        documentName: 'Affidavit_SA_Reyes.pdf',
        documentType: 'SupportingDocument',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: '41bb90cc12a4ff7031ab90c1e4d55aa2',
        documentLocation: '/SS8/eWarrant/doc-b2/Affidavit_SA_Reyes.pdf',
        sizeBytes: 657408,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Approved
      }
    ],
    services: [
      {
        reference: 'LIID-AZ-260702-0041',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'Active',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIandCC',
        handoverFormat: 'TS102232-2',
        deliveryEndpoint: '10.44.12.8:5000',
        // One destination carrying both streams, which is how an IRIandCC task hands
        // them to a single collector.
        ccDeliveryEndpoint: '10.44.12.8:5000',
        cspId: 'US-VZW-01',
        provisioningTime: '2026-07-02T00:00:00Z',
        deprovisioningTime: '2026-08-01T23:59:59Z',
        startTime: '2026-07-02T00:00:00Z',
        endTime: '2026-08-01T23:59:59Z',
        serviceTypes: ['Data'],
        targetIdentifiers: [
          { format: 'SUPIIMSI', value: '1234512345' },
        ]
      }
    ]
  },
  {
    id: 'csw-3011',
    status: EWarrantStatus.Pending,
    interceptStatus: EWarrantInterceptStatusValue.NotCreated,
    decidedAt: null,
    decidedBy: null,
    decisionNote: '',
    authorisation: {
      HI1OBJECTID: 'csw-3011',
      SENDERID: 'US-4B18E062',
      name: 'Intercept 5:26-cr-00733 · MVNO sweep (Verizon + Xfinity Mobile)',
      description: 'Multi-CSP tasking — same target roams the Verizon host network and an MVNO on it',
      sourceWarrantId: 'ew-2044',
      sourceAgency: 'US-4B18E062',
      receivingCsp: 'Verizon Wireless',
      receivedAt: '2026-07-21T09:02:00Z',
      reference: '5:26-cr-00733',
      priority: 'Normal',
      legalType: '',
      typeOfCase: 'OrganisedCrime',
      desiredStatus: 'AwaitingApproval',
      startTime: '2026-07-21T00:00:00Z',
      endTime: '2026-09-19T23:59:59Z',
      creationTimestamp: '2026-07-20T00:00:00Z',
      jurisdiction: 'Northern District of Georgia',
      legalEntity: 'FBI — Atlanta Field Office',
      cspId: 'US-VZW-01',
      countryCode: 'US',
      flags: ['IsConsensual'],
      approval: {
        type: 'Creation',
        reference: '5:26-cr-00733',
        approverName: 'Hon. P. A. Nyland',
        approverRole: 'Authorising Judicial Officer',
        approverEmail: 'p.nyland@vaed.uscourts.gov',
        approverPhone: '+1 757 222 7205',
        timestamp: '2026-07-20T00:00:00Z',
        isEmergency: false
      },
      manualInformation: 'Target device roams between VZW host network and Xfinity Mobile MVNO service.'
    },
    documents: [
      {
        docId: 'doc-e1',
        documentName: 'TitleIII_Pkg_5-26-cr-00733_SIGNED.pdf',
        documentType: 'Warrant',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: 'a812ffe0c39b2d6e0187ff33ab120ecd',
        documentLocation: '/SS8/eWarrant/doc-e1/TitleIII_Pkg_5-26-cr-00733_SIGNED.pdf',
        sizeBytes: 1102336,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Pending
      }
    ],
    services: [
      {
        reference: 'LIID-GA-260721-0044',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIandCC',
        handoverFormat: 'TS102232-3',
        deliveryEndpoint: '10.44.12.20:5000',
        ccDeliveryEndpoint: '10.44.12.20:5000',
        cspId: 'US-VZW-01',
        provisioningTime: '2026-07-21T00:00:00Z',
        deprovisioningTime: '2026-09-19T23:59:59Z',
        startTime: '2026-07-21T00:00:00Z',
        endTime: '2026-09-19T23:59:59Z',
        serviceTypes: ['Voice', 'Messaging', 'Data'],
        targetIdentifiers: [{ format: 'E164Number', value: '+14045550132' }]
      }
    ]
  },
  {
    id: 'csw-3012',
    status: EWarrantStatus.Pending,
    interceptStatus: EWarrantInterceptStatusValue.CreateUnderway,
    decidedAt: null,
    decidedBy: null,
    decisionNote: '',
    authorisation: {
      HI1OBJECTID: 'csw-3012',
      SENDERID: 'US-4B18E062',
      name: 'Intercept 5:26-cr-00733 · MVNO sweep (Verizon + Xfinity Mobile)',
      description: 'Multi-CSP tasking — same target roams the Verizon host network and an MVNO on it',
      sourceWarrantId: 'ew-2044',
      sourceAgency: 'US-4B18E062',
      receivingCsp: 'Xfinity Mobile',
      receivedAt: '2026-07-21T09:02:05Z',
      reference: '5:26-cr-00733',
      priority: 'Normal',
      legalType: '',
      typeOfCase: 'OrganisedCrime',
      desiredStatus: 'AwaitingApproval',
      startTime: '2026-07-21T00:00:00Z',
      endTime: '2026-09-19T23:59:59Z',
      creationTimestamp: '2026-07-20T00:00:00Z',
      jurisdiction: 'Northern District of Georgia',
      legalEntity: 'FBI — Atlanta Field Office',
      cspId: 'US-XFM-07',
      countryCode: 'US',
      flags: ['IsConsensual'],
      approval: {
        type: 'Creation',
        reference: '5:26-cr-00733',
        approverName: 'Hon. P. A. Nyland',
        approverRole: 'Authorising Judicial Officer',
        approverEmail: 'p.nyland@vaed.uscourts.gov',
        approverPhone: '+1 757 222 7205',
        timestamp: '2026-07-20T00:00:00Z',
        isEmergency: false
      },
      manualInformation: 'Target device roams between VZW host network and Xfinity Mobile MVNO service.'
    },
    documents: [
      {
        docId: 'doc-e2',
        documentName: 'TitleIII_Pkg_5-26-cr-00733_SIGNED.pdf',
        documentType: 'Warrant',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: 'a812ffe0c39b2d6e0187ff33ab120ecd',
        documentLocation: '/SS8/eWarrant/doc-e2/TitleIII_Pkg_5-26-cr-00733_SIGNED.pdf',
        sizeBytes: 1102336,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Pending
      }
    ],
    services: [
      {
        reference: 'LIID-GA-260721-0044',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIandCC',
        handoverFormat: 'TS102232-3',
        deliveryEndpoint: '10.44.12.21:5001',
        ccDeliveryEndpoint: '10.44.12.21:5001',
        cspId: 'US-XFM-07',
        provisioningTime: '2026-07-21T00:00:00Z',
        deprovisioningTime: '2026-09-19T23:59:59Z',
        startTime: '2026-07-21T00:00:00Z',
        endTime: '2026-09-19T23:59:59Z',
        serviceTypes: ['Voice', 'Messaging', 'Data'],
        targetIdentifiers: [{ format: 'E164Number', value: '+14045550132' }]
      }
    ]
  },
  {
    id: 'csw-3007',
    status: EWarrantStatus.Rejected,
    interceptStatus: EWarrantInterceptStatusValue.CreateError,
    decidedAt: '2026-07-16T16:41:00Z',
    decidedBy: 'csp.operator',
    decisionNote: 'MSISDN +1 757 555 0188 is not provisioned on this network — ported out 2026-05-30.',
    authorisation: {
      HI1OBJECTID: 'csw-3007',
      SENDERID: 'US-D93C7A15',
      name: 'Intercept 1:26-cr-00212 · AT&T Mobility',
      description: 'Pen register / trap & trace, Operation TIDEWATER',
      sourceWarrantId: 'ew-2039',
      sourceAgency: 'US-D93C7A15',
      receivingCsp: 'AT&T Mobility',
      receivedAt: '2026-07-16T16:05:00Z',
      reference: '1:26-cr-00212',
      priority: 'Normal',
      legalType: '',
      typeOfCase: 'OrganisedCrime',
      desiredStatus: 'AwaitingApproval',
      startTime: '2026-07-16T00:00:00Z',
      endTime: '2026-09-14T23:59:59Z',
      creationTimestamp: '2026-07-16T00:00:00Z',
      jurisdiction: 'Eastern District of Virginia',
      legalEntity: 'FBI — Norfolk Resident Agency',
      cspId: 'US-ATT-02',
      countryCode: 'US',
      flags: [],
      approval: {
        type: 'Creation',
        reference: '1:26-cr-00212',
        approverName: 'Hon. L. K. Praeger',
        approverRole: 'Authorising Judicial Officer',
        approverEmail: 'l.praeger@nyed.uscourts.gov',
        approverPhone: '+1 718 613 2300',
        timestamp: '2026-07-16T00:00:00Z',
        isEmergency: false
      },
      manualInformation: ''
    },
    documents: [
      {
        docId: 'doc-d1',
        documentName: 'PRTT_Order_1-26-cr-00212.pdf',
        documentType: 'Warrant',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: '5510bd44a0f1c3390b12ee7745fa9013',
        documentLocation: '/SS8/eWarrant/doc-d1/PRTT_Order_1-26-cr-00212.pdf',
        sizeBytes: 301056,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Rejected
      }
    ],
    services: [
      {
        reference: 'LIID-VA-260716-0039',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIOnly',
        handoverFormat: 'TS102232-2',
        deliveryEndpoint: '10.44.12.8:5000',
        // IRIOnly: no CC destination at all.
        ccDeliveryEndpoint: '',
        cspId: 'US-ATT-02',
        provisioningTime: '2026-07-16T00:00:00Z',
        deprovisioningTime: '2026-09-14T23:59:59Z',
        startTime: '2026-07-16T00:00:00Z',
        endTime: '2026-09-14T23:59:59Z',
        serviceTypes: ['Voice', 'Messaging'],
        targetIdentifiers: [{ format: 'E164Number', value: '+17575550188' }]
      }
    ]
  },
  {
    id: 'csw-3005',
    status: EWarrantStatus.Pending,
    interceptStatus: EWarrantInterceptStatusValue.NotCreated,
    decidedAt: null,
    decidedBy: null,
    decisionNote: '',
    authorisation: {
      HI1OBJECTID: 'csw-3005',
      SENDERID: 'US-2E7F4498',
      name: 'Intercept 3:26-mj-00877 · Xfinity Mobile',
      description: 'Emergency (18 U.S.C. §2518(7)) — kidnapping, exigent circumstances',
      sourceWarrantId: 'ew-2038',
      sourceAgency: 'US-2E7F4498',
      receivingCsp: 'Xfinity Mobile',
      receivedAt: '2026-07-18T07:31:00Z',
      reference: '3:26-mj-00877',
      priority: 'Urgent',
      legalType: '',
      typeOfCase: 'Other',
      desiredStatus: 'AwaitingApproval',
      startTime: '2026-07-18T00:00:00Z',
      endTime: '2026-07-20T23:59:59Z',
      creationTimestamp: '2026-07-18T00:00:00Z',
      jurisdiction: 'Northern District of Georgia',
      legalEntity: 'FBI — Atlanta Field Office',
      cspId: 'US-XFM-07',
      countryCode: 'US',
      flags: ['IsEmergency'],
      approval: {
        type: 'Creation',
        reference: '3:26-mj-00877',
        approverName: 'Hon. P. A. Nyland',
        approverRole: 'Authorising Judicial Officer',
        approverEmail: 'p.nyland@vaed.uscourts.gov',
        approverPhone: '+1 757 222 7205',
        timestamp: '2026-07-18T00:00:00Z',
        isEmergency: true
      },
      manualInformation: '48-hour emergency authorization. Real-time location required for victim recovery.'
    },
    documents: [
      {
        docId: 'doc-c1',
        documentName: 'Emergency_Auth_3-26-mj-00877.pdf',
        documentType: 'Warrant',
        contentType: 'application/pdf',
        checksumType: 'sha-256',
        checksum: 'cc10ee73da91b0284fc7710aa1902fd3',
        documentLocation: '/SS8/eWarrant/doc-c1/Emergency_Auth_3-26-mj-00877.pdf',
        sizeBytes: 397312,
        isPrimary: false,
        hasContent: true,
        docStatus: EWarrantStatus.Pending
      }
    ],
    services: [
      {
        reference: 'LIID-GA-260718-0038',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIandCC',
        handoverFormat: 'TS102232-3',
        deliveryEndpoint: '10.44.12.9:5000',
        ccDeliveryEndpoint: '10.44.12.9:5000',
        cspId: 'US-XFM-07',
        provisioningTime: '2026-07-18T00:00:00Z',
        deprovisioningTime: '2026-07-20T23:59:59Z',
        startTime: '2026-07-18T00:00:00Z',
        endTime: '2026-07-20T23:59:59Z',
        serviceTypes: ['Voice'],
        targetIdentifiers: [{ format: 'IMEI', value: '356938035643809' }]
      },
      {
        reference: 'LIID-GA-260718-0039',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'IRIandCC',
        handoverFormat: 'TS102232-3',
        deliveryEndpoint: '10.44.12.9:5001',
        ccDeliveryEndpoint: '10.44.12.9:5001',
        cspId: 'US-XFM-07',
        provisioningTime: '2026-07-18T00:00:00Z',
        deprovisioningTime: '2026-07-20T23:59:59Z',
        startTime: '2026-07-18T00:00:00Z',
        endTime: '2026-07-20T23:59:59Z',
        serviceTypes: ['Voice', 'Data'],
        targetIdentifiers: [
          { format: 'E164Number', value: '+14045550901' },
          { format: 'IMSI', value: '310004987654321' }
        ]
      },
      {
        reference: 'LIID-GA-260718-0040',
        actualStatus: 'AwaitingApproval',
        currentStatus: 'AwaitingApproval',
        taskStatus: 'AwaitingApproval',
        deliveryType: 'CCOnly',
        handoverFormat: 'TS102232-3',
        // CCOnly: the destination carries CC, so there is no IRI endpoint. Generation
        // reports the missing deliveryEndpoint for this service, which is the point —
        // a Collection Function's HI2 interface is the IRI destination.
        deliveryEndpoint: '',
        ccDeliveryEndpoint: '10.44.12.9:5002',
        cspId: 'US-XFM-07',
        provisioningTime: '2026-07-18T00:00:00Z',
        deprovisioningTime: '2026-07-20T23:59:59Z',
        startTime: '2026-07-18T00:00:00Z',
        endTime: '2026-07-20T23:59:59Z',
        serviceTypes: ['Data'],
        targetIdentifiers: [{ format: 'IPAddress', value: '203.0.113.44' }]
      }
    ]
  }
];

/** Simulated latency of the mock service, in milliseconds. */
const MOCK_LATENCY = 250;

function resolveWithMockLatency<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), MOCK_LATENCY);
  });
}

/**
 * EWARRANT_MOCK_LIST flattened back down to what the list endpoint answers with, so the
 * mock switch feeds the grid the same shape a real read does. Only the fields the
 * datasource and the reducer touch are built — it stands in for an axios response
 * rather than being one.
 */
function mockEWarrantListResponse(): AxiosResponse<ServerResponse<EWarrantListItem>> {
  const items: EWarrantListItem[] = EWARRANT_MOCK_LIST.map((eWarrant) => ({
    id: eWarrant.id,
    hi1ObjectId: eWarrant.id,
    senderId: eWarrant.authorisation.SENDERID,
    warrant: eWarrant.authorisation.reference,
    requestingAgency: eWarrant.authorisation.SENDERID,
    received: eWarrant.authorisation.receivedAt,
    status: eWarrant.status,
    interceptStatus: eWarrant.interceptStatus,
  }));

  return {
    data: {
      success: true,
      data: items,
      metadata: { recordCount: items.length, recordsInView: items.length },
    },
  } as unknown as AxiosResponse<ServerResponse<EWarrantListItem>>;
}

/* ==========================================================================
 * NAVIGATION
 * ========================================================================== */

export function goToEWarrantList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_EWARRANT_LIST,
    });

    history.push(EWARRANT_ROUTE);
  };
}

export function goToEWarrantView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_EWARRANT_VIEW,
    });

    history.push(`${EWARRANT_ROUTE}/view`);
  };
}

/* ==========================================================================
 * E-WARRANT LIST
 * ========================================================================== */

/**
 * Reads the e-warrants Xcipio holds, unless USE_EWARRANT_MOCK_LIST is on.
 *
 * The response travels unwrapped, as the grid's server side datasource needs it —
 * it reads the rows and the row count straight off the axios response. The reducer
 * widens those rows into EWarrant for the store, so the view still resolves the
 * e-warrant behind a selected row from there.
 *
 * deliveryFunctionId is not sent: the endpoint is not scoped to a delivery function,
 * but callers pass it so a reload still re-runs when it changes.
 *
 * @param query the `$top=…&$skip=…&$orderBy=…&$filter=…` the grid built from its
 *              paging, sorting and column filters. The backend maps the column names
 *              in it onto the EWSAUTH columns Xcipio filters and sorts on.
 */
export function getEWarrantList(deliveryFunctionId: string, query = ''): EWarrantActionTypes {
  if (USE_EWARRANT_MOCK_LIST) {
    return {
      type: GET_EWARRANT_LIST,
      payload: resolveWithMockLatency(mockEWarrantListResponse()),
    };
  }

  return {
    type: GET_EWARRANT_LIST,
    payload: api.getEWarrantList(query),
  };
}

/**
 * Reads one e-warrant in full. The list read carries no documents and no tasks, so the
 * view and the generate flow both depend on this; the result is merged onto the entity
 * the list already put in the store.
 */
export function getEWarrantDetail(hi1ObjectId: string, senderId: string): EWarrantActionTypes {
  if (USE_EWARRANT_MOCK_LIST) {
    // Matched on the whole key, the way the real read is: two of the mock e-warrants
    // share a SENDERID, so matching on the id alone would not be the same lookup.
    const mockEWarrant = EWARRANT_MOCK_LIST.find(
      ({ id, authorisation }) => id === hi1ObjectId && authorisation.SENDERID === senderId,
    );

    return {
      type: GET_EWARRANT_DETAIL,
      payload: mockEWarrant != null
        ? resolveWithMockLatency(mockEWarrant)
        : Promise.reject(new Error(`No mock e-warrant with id ${hi1ObjectId} for sender ${senderId}.`)),
    };
  }

  return {
    type: GET_EWARRANT_DETAIL,
    payload: api.getEWarrantDetail(hi1ObjectId, senderId)
      .then(({ data }) => adaptEWarrantDetailResponse(data.data?.[0] as EWarrantDetailResponse)),
  };
}

export function selectEWarrant(eWarrants: EWarrantGridRowData[]): SelectEWarrantAction {
  return {
    type: SELECT_EWARRANT,
    payload: eWarrants,
  };
}

/**
 * First step of the generate warrant flow: move the e-warrant's intercept status.
 * Real call — the backend reads the e-warrant out of Xcipio by this id, stamps the
 * status onto it and writes the whole row back.
 */
export function updateInterceptStatus(
  hi1ObjectId: string,
  senderId: string,
  interceptStatus: EWarrantInterceptStatus,
): EWarrantActionTypes {
  return {
    type: UPDATE_EWARRANT_INTERCEPT_STATUS,
    payload: api.updateInterceptStatus({
      HI1OBJECTID: hi1ObjectId,
      SENDERID: senderId,
      InterceptStatus: interceptStatus,
    }),
  };
}

/**
 * Records the operator's decision on an e-warrant in Xcipio — the backend reads the
 * EWSAUTH row by this id, stamps AUTHSTATUS with the decision and the decision note into
 * ERRMSG, and writes the whole row back.
 *
 * The per-document decisions travel with it. The backend writes each named document's
 * DOCSTATUS on its EWSDOCUMENT row *before* it moves AUTHSTATUS, and touches no document
 * at all when the list is empty — so a document that could not be written leaves the
 * e-warrant undecided and the whole decision retryable.
 *
 * Nothing in the store is touched by this: Xcipio owns the decision state, so callers
 * re-read the e-warrant once this has come back rather than patching the entity
 * themselves.
 *
 * startDateTime/endDateTime are only meaningful on an approval of an e-warrant whose raw
 * AUTHSTATUS is AwaitingUpdateApproval — their presence is what tells the backend to find
 * the warrant already provisioned for this e-warrant and revise its dates instead of
 * leaving it untouched, rather than the normal first-approval flow where Generate Warrant
 * creates one. Omitted, they are left off the payload entirely.
 */
export function updateAuthStatus(
  hi1ObjectId: string,
  senderId: string,
  authStatus: EWarrantDecisionStatus,
  messageText = '',
  documents: EWarrantDocumentStatusPayload[] = [],
  startDateTime?: string,
  endDateTime?: string,
): EWarrantActionTypes {
  return {
    type: UPDATE_EWARRANT_AUTH_STATUS,
    payload: api.updateAuthStatus({
      HI1OBJECTID: hi1ObjectId,
      SENDERID: senderId,
      AuthStatus: authStatus,
      MessageText: messageText,
      Documents: documents,
      ...(startDateTime != null && endDateTime != null
        ? { STARTDATETIME: startDateTime, ENDDATETIME: endDateTime }
        : {}),
    }),
  };
}

/**
 * Records the decision an operator made against one target of an e-warrant — the backend
 * finds that target's EWSLITASK row and moves both its TASKSTATUS and the task:Status its
 * ETSI payload carries to the decided status.
 *
 * The task is addressed by the LIID, which is what each service of the detail read carries
 * as its `reference`: the EWSLITASK table is read by the authorisation the tasks hang off,
 * so the LIID is what narrows that read to one target.
 *
 * Nothing in the store is touched by this, for the same reason updateAuthStatus touches
 * nothing: Xcipio owns the status, so callers re-read the e-warrant once this has come back
 * rather than patching the entity themselves.
 */
export function updateTaskStatus(
  hi1ObjectId: string,
  senderId: string,
  liid: string,
  taskStatus: EWarrantTaskStatus,
): EWarrantActionTypes {
  return {
    type: UPDATE_EWARRANT_TASK_STATUS,
    payload: api.updateTaskStatus({
      HI1OBJECTID: hi1ObjectId,
      SENDERID: senderId,
      LIID: liid,
      TaskStatus: taskStatus,
    }),
  };
}

/**
 * Asks the backend to generate the CSP side warrant for an approved e-warrant.
 * Unlike the rest of this module this is a real call — the e-warrant, the collection
 * functions built for it and its LEA are posted to /e-warrants/generate.
 *
 * @param settings what the portal already read from the backend and the backend would
 *                 otherwise have to read again — the ontology, what it enables here, and
 *                 the templates the generated cases point at.
 */
export function generateWarrant(
  eWarrant: EWarrant,
  collectionFunctions: CollectionFunction[],
  lea: CollectionFunctionGroup,
  settings?: {
    supportedFeatures?: SupportedFeatures
    enabledAfTypeList?: string[]
    enabledServices?: EnabledService[]
    templates?: EWarrantGenerateTemplate[]
    networks?: Network[]
  },
): EWarrantActionTypes {
  return {
    type: GENERATE_WARRANT,
    payload: api.generateWarrant({
      eWarrant,
      collectionFunctions,
      lea,
      ...settings,
    }),
  };
}

/**
 * Generates the CSP-side warrant document for an e-warrant and attaches it to
 * the e-warrant's document list. Mocked: the document is fabricated locally.
 */
export function generateEWarrant(id: string): EWarrantThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    const document: EWarrantDocument = await resolveWithMockLatency({
      docId: `doc-gen-${id}`,
      documentName: `Generated_Warrant_${id}.pdf`,
      documentType: 'GeneratedWarrant',
      contentType: 'application/pdf',
      checksumType: 'sha-256',
      checksum: '00000000000000000000000000000000',
      documentLocation: `/SS8/eWarrant/doc-gen-${id}/Generated_Warrant_${id}.pdf`,
      sizeBytes: 245760,
      isPrimary: false,
      hasContent: true,
      // Generated CSP-side, so it is not something the operator decides on.
      docStatus: EWarrantStatus.Approved,
    });

    dispatch({
      type: GENERATE_EWARRANT_FULFILLED,
      payload: { id, document },
    });
  };
}
